import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { AlertButtonColor, DialogService } from "@obg/mat.ui.core";
import { IauiVerificationAction, IauiVerificationSelector } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AuthSelector,
	BankDetailsVerificationStatus,
	CORE_ROUTE,
	CoreState,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteAction } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, merge, Observable, of } from "rxjs";
import { filter, map, pairwise, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { IauiVerificationDialogContainer } from "./iaui-verification-dialog.container";
import {
	IAUI_VERIFICATION_OPEN_DENIED_ALERT_ID,
	IAUI_VERIFICATION_OPEN_PENDING_ALERT_ID,
} from "./iaui-verification.const";

@Injectable({
	providedIn: "root",
})
export class IauiVerificationService {
	private afterLogin$ = this.store.select(AuthSelector.isAuthenticated).pipe(
		pairwise(),
		filter(([prev, next]) => !prev && next)
	);

	constructor(
		private store: Store<CoreState>,
		private dialogService: DialogService,
		private iauiVerificationSelector: IauiVerificationSelector,
		private iauiVerificationAction: IauiVerificationAction,
		private translatableAlertService: AbstractTranslatableAlertService,
		private deviceConfigService: DeviceConfigService,
		private customerSupportService: CustomerSupportService
	) {}

	initialize$() {
		const verification$ = this.afterLogin$.pipe(
			switchMap(() =>
				combineLatest([
					this.store.select(JurisdictionSelector.get).pipe(ignoreNil()),
					this.store.select(CustomerManagementSelector.getCustomer).pipe(
						ignoreNil(),
						map((customer) => customer.profile.bankDetailsVerificationStatus)
					),
					this.store.select(AuthSelector.isFirstLogin),
				]).pipe(
					filter(
						([jurisdiction, status, isFirstLogin]) =>
							jurisdiction === Jurisdiction.Iaui &&
							status === BankDetailsVerificationStatus.NotChecked &&
							!isFirstLogin
					),
					take(1),
					tap(() => this.store.dispatch(this.iauiVerificationAction.openModal())),
					map(() => undefined)
				)
			)
		);

		return merge(this.initModal$(), verification$);
	}

	initModal$(): Observable<void> {
		return this.store.select(this.iauiVerificationSelector.isModalOpen()).pipe(
			filter((isOpen) => isOpen),
			withLatestFrom(
				this.store.select(CustomerManagementSelector.getCustomer).pipe(
					ignoreNil(),
					map((customer) => customer.profile.bankDetailsVerificationStatus)
				)
			),
			switchMap(([, status]) => {
				switch (status) {
					case BankDetailsVerificationStatus.Denied:
						return this.openDeniedAlert$();
					case BankDetailsVerificationStatus.Pending:
						return this.openPendingAlert$();
					default:
						return this.openVerificationDialog$();
				}
			})
		);
	}

	private openDeniedAlert$(): Observable<void> {
		this.translatableAlertService.open(
			{
				id: IAUI_VERIFICATION_OPEN_DENIED_ALERT_ID,
				buttons: [
					{
						text: this.deviceConfigService.isDesktop()
							? "general.contact-customer-support"
							: "general.contact",
						handler: () => {
							this.store.dispatch(this.iauiVerificationAction.closeModal());
							this.customerSupportService.openSupportPageInNewTab();
						},
					},
					{
						text: "general.close",
						color: AlertButtonColor.Primary,
						handler: () => this.store.dispatch(this.iauiVerificationAction.closeModal()),
					},
				],
				message: "customer-verification.notification.verification-denied",
				hideClose: true,
			},
			[]
		);

		return of(undefined);
	}

	private openPendingAlert$(): Observable<void> {
		this.translatableAlertService.open(
			{
				id: IAUI_VERIFICATION_OPEN_PENDING_ALERT_ID,
				buttons: [
					{
						text: "general.close",
						color: AlertButtonColor.Primary,
						handler: () => this.store.dispatch(this.iauiVerificationAction.closeModal()),
					},
				],
				message: "customer-verification.notification.verification-pending",
				hideClose: true,
			},
			[]
		);
		return of(undefined);
	}

	private openVerificationDialog$(): Observable<void> {
		return this.dialogService
			.openComponent(IauiVerificationDialogContainer, { disableClose: true })
			.afterClosed()
			.pipe(
				take(1),
				tap((result) => {
					if (result?.paymentType) {
						this.store.dispatch(RouteAction.navigate(CORE_ROUTE.payment.deposit));
					}

					this.store.dispatch(this.iauiVerificationAction.closeModal(result?.paymentType));
				})
			);
	}
}
